import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { 
  BarChart3, Users, Loader2, TrendingUp, TrendingDown,
  Minus, Target, Award
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

export default function PerformanceComparison({ athletes = [], teamId }) {
  const [selectedAthletes, setSelectedAthletes] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [availableAthletes, setAvailableAthletes] = useState(athletes);

  useEffect(() => {
    if (teamId && athletes.length === 0) {
      fetchTeamAthletes();
    }
  }, [teamId]);

  const fetchTeamAthletes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/children?team_id=${teamId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableAthletes(response.data.children || []);
    } catch (err) {
      console.error('Failed to fetch athletes:', err);
    }
  };

  const compareAthletes = async () => {
    if (selectedAthletes.length < 2) {
      toast.error('Select at least 2 athletes to compare');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/comparison/athletes`, {
        athlete_ids: selectedAthletes,
        include_team_average: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComparison(response.data);
    } catch (err) {
      toast.error('Failed to compare athletes');
    } finally {
      setLoading(false);
    }
  };

  const toggleAthleteSelection = (id) => {
    if (selectedAthletes.includes(id)) {
      setSelectedAthletes(selectedAthletes.filter(a => a !== id));
    } else if (selectedAthletes.length < 5) {
      setSelectedAthletes([...selectedAthletes, id]);
    } else {
      toast.error('Maximum 5 athletes can be compared');
    }
  };

  const getBestValue = (values, testName) => {
    // For time-based tests, lower is better
    const timeBased = ['sprint', 'agility', '5_0_5', 't_test'];
    const isTimeBased = timeBased.some(t => testName.toLowerCase().includes(t));
    
    const numericValues = values.filter(v => v.value !== null).map(v => v.value);
    if (numericValues.length === 0) return null;
    
    return isTimeBased ? Math.min(...numericValues) : Math.max(...numericValues);
  };

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6" data-testid="performance-comparison">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl flex items-center justify-center">
            <BarChart3 className="h-5 w-5 text-purple-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Performance Comparison</h3>
            <p className="text-xs text-zinc-500">Compare athletes side-by-side</p>
          </div>
        </div>
        <Button
          onClick={compareAthletes}
          disabled={selectedAthletes.length < 2 || loading}
          className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm disabled:opacity-50"
          data-testid="compare-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <BarChart3 className="h-4 w-4 mr-1" />}
          Compare ({selectedAthletes.length})
        </Button>
      </div>

      {/* Athlete Selection */}
      {!comparison && (
        <div className="space-y-2 max-h-[300px] overflow-y-auto">
          <p className="text-sm text-zinc-400 mb-2">Select 2-5 athletes to compare:</p>
          {availableAthletes.map(athlete => (
            <button
              key={athlete.id}
              onClick={() => toggleAthleteSelection(athlete.id)}
              className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${
                selectedAthletes.includes(athlete.id)
                  ? 'bg-purple-500/20 border border-purple-500/50'
                  : 'bg-zinc-800/50 border border-zinc-700/50 hover:bg-zinc-800'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-xs">
                  {athlete.first_name?.charAt(0)}{athlete.last_name?.charAt(0)}
                </div>
                <span className="text-white">{athlete.first_name} {athlete.last_name}</span>
              </div>
              {selectedAthletes.includes(athlete.id) && (
                <span className="text-purple-400 text-xs">Selected</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Comparison Results */}
      {comparison && (
        <div className="space-y-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setComparison(null)}
            className="text-zinc-400 hover:text-white"
          >
            ← Back to selection
          </Button>

          {/* Athletes Header */}
          <div className="grid gap-2" style={{gridTemplateColumns: `120px repeat(${comparison.athletes.length}, 1fr)`}}>
            <div className="text-xs text-zinc-500 font-medium">Test</div>
            {comparison.athletes.map(athlete => (
              <div key={athlete.id} className="text-center">
                <p className="text-sm font-medium text-white truncate">{athlete.name}</p>
                <p className="text-xs text-zinc-500">{athlete.team || 'No team'}</p>
              </div>
            ))}
          </div>

          {/* Comparison Matrix */}
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {comparison.comparison.map((row, idx) => {
              const bestValue = getBestValue(row.values, row.test_name);
              
              return (
                <div 
                  key={idx} 
                  className="grid gap-2 p-3 bg-zinc-800/50 rounded-lg"
                  style={{gridTemplateColumns: `120px repeat(${comparison.athletes.length}, 1fr)`}}
                >
                  <div className="text-xs text-zinc-400 font-medium self-center">
                    {row.test_name}
                  </div>
                  {row.values.map((val, vidx) => {
                    const isBest = val.value === bestValue && val.value !== null;
                    return (
                      <div key={vidx} className="text-center">
                        <span className={`text-sm font-medium ${
                          isBest ? 'text-green-400' : val.value ? 'text-white' : 'text-zinc-600'
                        }`}>
                          {val.value ?? '-'}
                        </span>
                        {val.grade && (
                          <span className="text-xs text-zinc-500 ml-1">
                            (G{val.grade})
                          </span>
                        )}
                        {isBest && <Award className="h-3 w-3 text-green-400 inline ml-1" />}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Team Average */}
          {comparison.team_average && (
            <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
              <p className="text-sm font-medium text-blue-400 mb-2">
                Team Average ({comparison.team_average.team_name})
              </p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(comparison.team_average.averages || {}).slice(0, 6).map(([key, value]) => (
                  <span key={key} className="px-2 py-1 bg-blue-500/20 rounded text-xs text-blue-300">
                    {key.replace('_', ' ')}: {value}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
